// `station.client.update` — "Update now" (F-STN-12; spec/10 §8.2, spec/09 §6.2; the owner, 2026-10-07: "Can we have an
// option on app.clavian.io to update manually?").
//
// A station installs a new client release on its own schedule: it asks `GET /v1/station/client-update` on (re)connect
// and every 30 minutes, and installs the MSI only when it has been locked with no session for five minutes or is inside
// the venue's `station.clientUpdateWindow` (spec/10 §8). Staff with `stations:maintenance` may now ask one station to
// do it **now** (`POST …/stations/{s}/client-update/install-now`): the api sends this command, and the station
//
//   - checks at once (the 30-minute schedule is bypassed), verifies the signed manifest as always, and downloads what
//     the answer offers;
//   - installs as soon as it has no session — skipping the five idle minutes and the update window — but NEVER during
//     one (`in_session`, `session_locked`, `offline_locked`): with a session running it says `deferred` and installs
//     the moment the station is back to `locked` with no session;
//   - keeps every other safety: the signed manifest and hashes, the rollback package rule (RollbackPackageDecision),
//     a version it rolled back from, `minServiceVersion`, online, and the helper's health check and rollback.
//
// The station answers within the ack window (spec/09 §5.1) with `StationClientUpdateCommandResult` — or, when the
// update cannot happen, `ok: false` with one of `STATION_CLIENT_UPDATE_ERROR_CODES`. Downloading and installing take
// longer than an ack: their progress is the client-update status the station already reports
// (`PUT /v1/station/client-update-status`), which the console's Station client section shows.
//
// A station built before this command answers `ok: false` `unsupported` ("the station does not implement
// station.client.update"): the console says that PC needs a newer app first, and it still updates on its own schedule.
import { z } from "../zod.js";
import { StationModeSchema } from "./station.js";
/** The command type (spec/09 §6.2). Its default expiry is 24 hours (`STATION_COMMAND_EXPIRY_SECONDS`). */
export const STATION_CLIENT_UPDATE = 'station.client.update';
/**
 * Why the command was sent. `install_now`: staff asked for it from the console. `update_image` (2026-10-07, spec/10
 * §14.3): staff say this diskless PC was started as CCBoot super client to update the image — the station marks this boot
 * as an image-maintenance boot (until it restarts) and installs, so the image keeps the update; a diskless client asked
 * `install_now` refuses (`update_refused`), because what it installs is lost when it restarts. A station built before
 * `update_image` answers `invalid_payload`.
 */
export const STATION_CLIENT_UPDATE_REASONS = ['install_now', 'update_image'];
export const StationClientUpdateCommandPayloadSchema = z
    .object({
    reason: z.enum(STATION_CLIENT_UPDATE_REASONS),
})
    .openapi('StationClientUpdateCommandPayload', {
    description: '`station.client.update`: check for this station’s release now and install it as soon as no session runs (spec/09 §6.2, spec/10 §8.2).',
});
/**
 * What the station did with the command, as its ack's `result` says (spec/10 §8.2):
 *
 *   up_to_date  the answer offers nothing newer to install: the station runs what it should (a kiosk-only change still
 *               swaps on its own, with no restart)
 *   started     a release is offered and installs now — downloaded first when it is not on disk yet; the station's
 *               software restarts for about a minute
 *   deferred    a release is offered but the station is busy — a session is running (or it is in maintenance, disabled,
 *               pairing): it installs as soon as the station is back to `locked` with no session, with no idle wait
 *   checking    the check had not finished inside the ack window (a slow link, or the updater busy downloading); it
 *               carries on, and the client-update status shows what it decides
 */
export const STATION_CLIENT_UPDATE_OUTCOMES = ['up_to_date', 'started', 'deferred', 'checking'];
export const StationClientUpdateCommandResultSchema = z
    .object({
    outcome: z.enum(STATION_CLIENT_UPDATE_OUTCOMES),
    /** The client version the station runs now. */
    clientVersion: z.string().min(1).max(64),
    /** The release it installs (`started`, `deferred`); null when there is none or it does not know yet. */
    targetVersion: z.string().min(1).max(64).nullable(),
    /** The station's mode when it answered: why a `deferred` install waits (a session, maintenance…). */
    mode: StationModeSchema,
})
    .openapi('StationClientUpdateCommandResult', {
    description: 'What a station did with `station.client.update`: up to date, started, deferred until it is free, or still checking (spec/10 §8.2).',
});
/**
 * The `error.code`s of a failed `station.client.update` ack:
 *
 *   update_refused  a release is offered but this station may not install it: the manifest did not verify, it needs a
 *                   newer service first (`minServiceVersion`), the station rolled back from it before, there is no
 *                   rollback package for the running version, or it is a diskless client (CCBoot) asked `install_now` —
 *                   its image is updated instead. `message` says which, as the client-update status does.
 *   check_failed    the station could not reach the api to check (it retries on its own schedule)
 *   offline         the station's link is down, so it cannot check now (it checks when it reconnects)
 *   not_paired      the station has no identity yet
 *   unsupported     a station built before this command (the router's answer for any command it does not know)
 */
export const STATION_CLIENT_UPDATE_ERROR_CODES = ['update_refused', 'check_failed', 'offline', 'not_paired', 'unsupported'];
//# sourceMappingURL=client-update.js.map